/**
 * Bridges the feedback categories to the global FormType records that own their input format.
 *
 * Each category is backed by a FormType authored in csv (`en/FormType.csv`): `feedbackProblem`,
 * `feedbackIdea` and `feedbackGeneral`. A category is only offered when its FormType reached the
 * client, so an account that has not been provisioned a form simply does not see that option.
 */
import { v4 as uuidV4 } from 'uuid';
import { CATEGORY_IDS, GENERAL_CATEGORY_ID, IDEA_CATEGORY_ID, PROBLEM_CATEGORY_ID, PRODUCT_CATEGORY_ID } from './feedbackConstants';
import { getFeedbackCategory } from './feedbackCopy';
export const FEEDBACK_FORM_TYPE_IDENTIFIERS = {
  [PROBLEM_CATEGORY_ID]: 'feedbackProblem',
  [IDEA_CATEGORY_ID]: 'feedbackIdea',
  [GENERAL_CATEGORY_ID]: 'feedbackGeneral',
  [PRODUCT_CATEGORY_ID]: 'feedbackTrc'
};

/**
 * FormType records carry `formatInput` as a JSON string; tests and behaviors hand it over already
 * parsed. A format that cannot be parsed leaves its category unavailable rather than rendering an
 * empty form.
 */
export const parseFormatInput = formatInput => {
  if (!formatInput) {
    return null;
  }
  let format = formatInput;
  if (typeof format === 'string') {
    try {
      format = JSON.parse(format);
    } catch (_unused) {
      return null;
    }
  }
  const isUsable = typeof format === 'object' && !Array.isArray(format) && Object.keys(format).length > 0;
  return isUsable ? format : null;
};

/**
 * Categories that compete for the single Feedback slot, in priority order: a customer-specific
 * form replaces the shared one when both are provisioned.
 */
const FEEDBACK_SLOT_CATEGORY_IDS = [PRODUCT_CATEGORY_ID, GENERAL_CATEGORY_ID];
const isFeedbackSlotCategory = categoryId => FEEDBACK_SLOT_CATEGORY_IDS.includes(categoryId);
const toFeedbackForm = (categoryId, formTypes) => {
  const identifier = FEEDBACK_FORM_TYPE_IDENTIFIERS[categoryId];
  const formType = formTypes.find(candidate => (candidate === null || candidate === void 0 ? void 0 : candidate.identifier) === identifier);
  if (!formType) {
    return null;
  }
  const format = parseFormatInput(formType.formatInput);
  if (!format) {
    return null;
  }
  const {
    optionTitle
  } = getFeedbackCategory(categoryId);
  return {
    categoryId,
    identifier,
    // Every Feedback-slot form is labelled "Feedback" whatever its FormType is named, so a
    // customer-specific form is indistinguishable from the shared one. Problem and Idea keep the
    // label the account was provisioned.
    name: isFeedbackSlotCategory(categoryId) ? optionTitle : formType.name || optionTitle,
    format
  };
};

/**
 * Feedback forms in picker order - Problem, Idea, then one Feedback slot. Traceability's
 * feedbackTrc form replaces feedbackGeneral in that slot when both are provisioned. Clients still
 * submit category "product" so the server can select feedbackTrc; the server then stores the
 * shared "general" Feedback category on the form.
 */
export const buildFeedbackForms = (formTypes = []) => {
  const primaryForms = CATEGORY_IDS.filter(categoryId => !isFeedbackSlotCategory(categoryId)).map(categoryId => toFeedbackForm(categoryId, formTypes)).filter(Boolean);
  const feedbackForm = FEEDBACK_SLOT_CATEGORY_IDS.map(categoryId => toFeedbackForm(categoryId, formTypes)).find(Boolean);
  return feedbackForm ? [...primaryForms, feedbackForm] : primaryForms;
};
export const selectFeedbackForms = state => {
  var _state$formType;
  return buildFeedbackForms(state === null || state === void 0 || (_state$formType = state.formType) === null || _state$formType === void 0 ? void 0 : _state$formType.list);
};

/**
 * Build the form-modal draft the generic editor edits. `activeCache.formatInput` keeps the
 * unevaluated format so update behaviors and format re-evaluation run the same way they do for
 * any other form.
 */
export const buildFeedbackDraft = (format, id = uuidV4().toUpperCase()) => ({
  active: {
    id,
    format,
    content: {}
  },
  activeCache: {
    formatInput: format
  }
});